import { api } from './authService';

// 获取当前租户的API基础路径
function getApiBase(): string {
  const tenantCode = localStorage.getItem('currentTenant') || 'default';
  return `/${tenantCode}`;
}

// WhatsApp账号接口
export interface WhatsAppAccount {
  id: number;
  tenant_code: string;
  account_name: string;
  account_type: string;
  phone_number: string;
  webhook_url?: string;
  webhook_token?: string;
  access_token?: string;
  verify_token?: string;
  business_account_id?: string;
  priority: number;
  max_concurrent_chats: number;
  working_hours?: any;
  auto_reply_config?: any;
  partner_id?: number;
  partner_name?: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

// Agent平台接口
export interface AgentPlatform {
  id: number;
  tenant_code: string;
  platform_name: string;
  platform_type: string;
  app_name: string;
  app_type: string;
  api_key: string;
  base_url: string;
  app_id?: string;
  model_name?: string;
  max_tokens?: number;
  temperature?: number;
  system_prompt?: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

// WhatsApp账号到Agent的映射接口
export interface WhatsAppAccountMapping {
  id: number;
  tenant_code: string;
  whatsapp_account_id: number;
  service_id: number; // 关联的服务 ID
  agent_id: number; // 关联的 Agent ID
  agent_name?: string; // Agent 名称（扩展字段）
  service_name?: string; // 服务名称（扩展字段）
  platform_type?: string; // 平台类型（扩展字段）
  agent_config?: any;
  processing_mode: 'sync' | 'async';
  timeout_seconds: number;
  max_retries: number;
  retry_delay_seconds: number;
  webhook_url?: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

// 创建WhatsApp账号请求
export interface CreateWhatsAppAccountRequest {
  tenant_code: string;
  accountName: string;
  accountType: string;
  phoneNumber: string;
  webhookUrl?: string;
  webhookToken?: string;
  accessToken?: string;
  verifyToken?: string;
  businessAccountId?: string;
  priority?: number;
  maxConcurrentChats?: number;
  workingHours?: any;
  autoReplyConfig?: any;
  isActive?: boolean;
}

// 更新WhatsApp账号请求
export interface UpdateWhatsAppAccountRequest {
  accountName?: string;
  accountType?: string;
  phoneNumber?: string;
  webhookUrl?: string;
  webhookToken?: string;
  accessToken?: string;
  verifyToken?: string;
  businessAccountId?: string;
  priority?: number;
  maxConcurrentChats?: number;
  workingHours?: any;
  autoReplyConfig?: any;
  isActive?: boolean;
}

// 创建Agent平台请求
export interface CreateAgentPlatformRequest {
  tenant_code: string;
  platform_name: string;
  platform_type: string;
  app_name: string;
  app_type: string;
  api_key: string;
  base_url: string;
  app_id?: string;
  model_name?: string;
  max_tokens?: number;
  temperature?: number;
  system_prompt?: string;
  is_active?: boolean;
}

// 更新Agent平台请求
export interface UpdateAgentPlatformRequest {
  platform_name?: string;
  platform_type?: string;
  app_name?: string;
  app_type?: string;
  api_key?: string;
  base_url?: string;
  app_id?: string;
  model_name?: string;
  max_tokens?: number;
  temperature?: number;
  system_prompt?: string;
  is_active?: boolean;
}

// 创建映射请求
export interface CreateAccountMappingRequest {
  tenant_code: string;
  whatsapp_account_id: number;
  service_id: number; // 关联的服务 ID
  agent_id: number; // 关联的 Agent ID
  agent_config?: any;
  processing_mode: 'sync' | 'async';
  timeout_seconds?: number;
  max_retries?: number;
  retry_delay_seconds?: number;
  webhook_url?: string;
  is_active?: boolean;
}

// 更新映射请求
export interface UpdateAccountMappingRequest {
  agent_id?: string;
  agent_name?: string;
  agent_config?: any;
  processing_mode?: 'sync' | 'async';
  timeout_seconds?: number;
  max_retries?: number;
  retry_delay_seconds?: number;
  webhook_url?: string;
  is_active?: boolean;
}

// 测试连接结果
export interface TestConnectionResult {
  success: boolean;
  message?: string;
  error?: string;
  response_time?: number;
}

// WhatsApp Partner接口
export interface WhatsAppPartner {
  id: number;
  tenantCode: string;
  partnerName: string;
  partnerType: 'meta_official' | '360dialog' | 'twilio' | 'messagebird' | 'custom';
  partnerCode: string;
  apiEndpoint?: string;
  apiKey?: string;
  accessToken?: string;
  config?: any;
  isActive: boolean;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

// 创建WhatsApp Partner请求
export interface CreateWhatsAppPartnerRequest {
  tenantCode: string;
  partnerName: string;
  partnerType: 'meta_official' | '360dialog' | 'twilio' | 'messagebird' | 'custom';
  partnerCode: string;
  apiEndpoint?: string;
  apiKey?: string;
  accessToken?: string;
  config?: any;
  description?: string;
}

// 更新WhatsApp Partner请求
export interface UpdateWhatsAppPartnerRequest {
  partnerName?: string;
  partnerType?: 'meta_official' | '360dialog' | 'twilio' | 'messagebird' | 'custom';
  partnerCode?: string;
  apiEndpoint?: string;
  apiKey?: string;
  accessToken?: string;
  config?: any;
  isActive?: boolean;
  description?: string;
}

// WhatsApp Connector服务状态
export interface ConnectorStatus {
  tenant: string;
  timestamp: string;
  config: {
    agentPlatforms: number;
    whatsappAccounts: number;
    employees: number;
    accountMappings: number;
  };
  retryQueue: any;
  services: {
    configManager: string;
    messageProcessor: string;
    retryManager: string;
    database: string;
  };
}

export const whatsappConnectorService = {
  // 获取所有WhatsApp账号
  async getWhatsAppAccounts(): Promise<{ data: WhatsAppAccount[]; total?: number }> {
    const response = await api.get(`${getApiBase()}/whatsapp/accounts`);
    return response.data;
  },

  // 根据ID获取WhatsApp账号
  async getWhatsAppAccount(id: number): Promise<WhatsAppAccount> {
    const response = await api.get(`${getApiBase()}/whatsapp/accounts/${id}`);
    return response.data.account || response.data;
  },

  // 创建WhatsApp账号
  async createWhatsAppAccount(data: CreateWhatsAppAccountRequest): Promise<WhatsAppAccount> {
    const response = await api.post(`${getApiBase()}/whatsapp/accounts`, data);
    return response.data.account || response.data;
  },

  // 更新WhatsApp账号
  async updateWhatsAppAccount(id: number, data: UpdateWhatsAppAccountRequest): Promise<WhatsAppAccount> {
    const response = await api.put(`${getApiBase()}/whatsapp/accounts/${id}`, data);
    return response.data.account || response.data;
  },

  // 删除WhatsApp账号
  async deleteWhatsAppAccount(id: number): Promise<void> {
    await api.delete(`${getApiBase()}/whatsapp/accounts/${id}`);
  },

  // 获取所有Agent平台
  async getAgentPlatforms(): Promise<{ data: AgentPlatform[]; total?: number }> {
    const response = await api.get(`${getApiBase()}/whatsapp/platforms`);
    return response.data;
  },

  // 根据ID获取Agent平台
  async getAgentPlatform(id: number): Promise<AgentPlatform> {
    const response = await api.get(`${getApiBase()}/whatsapp/platforms/${id}`);
    return response.data.platform || response.data;
  },

  // 创建Agent平台
  async createAgentPlatform(data: CreateAgentPlatformRequest): Promise<AgentPlatform> {
    const response = await api.post(`${getApiBase()}/whatsapp/platforms`, data);
    return response.data.platform || response.data;
  },

  // 更新Agent平台
  async updateAgentPlatform(id: number, data: UpdateAgentPlatformRequest): Promise<AgentPlatform> {
    const response = await api.put(`${getApiBase()}/whatsapp/platforms/${id}`, data);
    return response.data.platform || response.data;
  },

  // 删除Agent平台
  async deleteAgentPlatform(id: number): Promise<void> {
    await api.delete(`${getApiBase()}/whatsapp/platforms/${id}`);
  },

  // 获取所有账号映射
  async getAccountMappings(): Promise<{ data: WhatsAppAccountMapping[]; total?: number }> {
    const response = await api.get(`${getApiBase()}/whatsapp/mappings`);
    return response.data;
  },

  // 根据ID获取映射
  async getAccountMapping(id: number): Promise<WhatsAppAccountMapping> {
    const response = await api.get(`${getApiBase()}/whatsapp/mappings/${id}`);
    return response.data.mapping || response.data;
  },

  // 创建账号映射
  async createAccountMapping(data: CreateAccountMappingRequest): Promise<WhatsAppAccountMapping> {
    const response = await api.post(`${getApiBase()}/whatsapp/mappings`, data);
    return response.data.mapping || response.data;
  },

  // 更新账号映射
  async updateAccountMapping(id: number, data: UpdateAccountMappingRequest): Promise<WhatsAppAccountMapping> {
    const response = await api.put(`${getApiBase()}/whatsapp/mappings/${id}`, data);
    return response.data.mapping || response.data;
  },

  // 删除账号映射
  async deleteAccountMapping(id: number): Promise<void> {
    await api.delete(`${getApiBase()}/whatsapp/mappings/${id}`);
  },

  // 获取Connector状态
  async getConnectorStatus(): Promise<ConnectorStatus> {
    const tenantCode = localStorage.getItem('currentTenant') || 'default';
    const response = await api.get(`/${tenantCode}/whatsappapi/status`);
    return response.data.data || response.data;
  },

  // 重新加载Connector配置
  async reloadConnectorConfig(tenant?: string): Promise<{ success: boolean; message: string }> {
    const tenantCode = tenant || localStorage.getItem('currentTenant') || 'default';
    try {
      console.log('🔄 发送配置刷新请求', { tenantCode, url: `/${tenantCode}/whatsappapi/health/reload-config` });
      const response = await api.post(`/${tenantCode}/whatsappapi/health/reload-config`, { tenant: tenantCode });
      console.log('✅ 配置刷新响应', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ 配置刷新失败', {
        tenantCode,
        url: `/${tenantCode}/whatsappapi/health/reload-config`,
        error: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      throw error;
    }
  },

  // 测试WhatsApp账号连接
  async testWhatsAppConnection(accountId: number): Promise<TestConnectionResult> {
    const response = await api.post(`${getApiBase()}/whatsapp/accounts/${accountId}/test`);
    return response.data;
  },

  // 测试Agent平台连接
  async testAgentPlatformConnection(platformId: number): Promise<TestConnectionResult> {
    const response = await api.post(`${getApiBase()}/whatsapp/platforms/${platformId}/test`);
    return response.data;
  },

  // 发送测试消息
  async sendTestMessage(accountId: number, to: string, message: string): Promise<any> {
    const tenantCode = localStorage.getItem('currentTenant') || 'default';
    const response = await api.post(`/${tenantCode}/whatsappapi/send/${accountId}`, {
      to,
      message
    });
    return response.data;
  },

  // 获取重试队列统计
  async getRetryStats(): Promise<any> {
    const tenantCode = localStorage.getItem('currentTenant') || 'default';
    const response = await api.get(`/${tenantCode}/whatsappapi/stats`);
    return response.data;
  },

  // 手动重试消息
  async retryMessage(messageId: number): Promise<any> {
    const response = await api.post(`${getApiBase()}/whatsapp/retry/${messageId}`);
    return response.data;
  },

  // 获取消息列表
  async getMessages(params?: {
    page?: number;
    pageSize?: number;
    status?: string;
    accountId?: number;
    startDate?: string;
    endDate?: string;
  }): Promise<any> {
    const tenantCode = localStorage.getItem('currentTenant') || 'default';
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    const queryString = queryParams.toString();
    const url = queryString
      ? `/${tenantCode}/whatsappapi/messages?${queryString}`
      : `/${tenantCode}/whatsappapi/messages`;

    const response = await api.get(url);
    return response.data;
  },

  // 获取异步任务列表
  async getAsyncTasks(params?: {
    page?: number;
    pageSize?: number;
    status?: string;
    accountId?: number;
    startDate?: string;
    endDate?: string;
  }): Promise<any> {
    const tenantCode = localStorage.getItem('currentTenant') || 'default';
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    const queryString = queryParams.toString();
    const url = queryString
      ? `/${tenantCode}/whatsappapi/async-tasks?${queryString}`
      : `/${tenantCode}/whatsappapi/async-tasks`;

    const response = await api.get(url);
    return response.data;
  },

  // 获取消息详情和处理日志
  async getMessageDetail(messageId: string): Promise<any> {
    const tenantCode = localStorage.getItem('currentTenant') || 'default';
    const response = await api.get(`/${tenantCode}/whatsappapi/messages/${messageId}`);
    return response.data;
  },

  // 获取异步任务详情
  async getAsyncTaskDetail(taskId: string): Promise<any> {
    const tenantCode = localStorage.getItem('currentTenant') || 'default';
    const response = await api.get(`/${tenantCode}/whatsappapi/async-tasks/${taskId}`);
    return response.data;
  },

  // 重新发送失败消息
  async resendMessage(messageId: string): Promise<any> {
    const tenantCode = localStorage.getItem('currentTenant') || 'default';
    const response = await api.post(`/${tenantCode}/whatsappapi/messages/${messageId}/resend`);
    return response.data;
  },

  // 获取处理统计
  async getProcessingStats(): Promise<any> {
    const tenantCode = localStorage.getItem('currentTenant') || 'default';
    const response = await api.get(`/${tenantCode}/whatsappapi/stats`);
    return response.data;
  },

  // ========== WhatsApp Partner 管理 ==========
  
  // 获取所有WhatsApp Partners
  async getWhatsAppPartners(): Promise<{ data: WhatsAppPartner[]; total?: number }> {
    const response = await api.get(`${getApiBase()}/whatsapp/partners`);
    return response.data;
  },

  // 根据ID获取WhatsApp Partner
  async getWhatsAppPartner(id: number): Promise<WhatsAppPartner> {
    const response = await api.get(`${getApiBase()}/whatsapp/partners/${id}`);
    return response.data.partner || response.data;
  },

  // 创建WhatsApp Partner
  async createWhatsAppPartner(data: CreateWhatsAppPartnerRequest): Promise<WhatsAppPartner> {
    const response = await api.post(`${getApiBase()}/whatsapp/partners`, data);
    return response.data.partner || response.data;
  },

  // 更新WhatsApp Partner
  async updateWhatsAppPartner(id: number, data: UpdateWhatsAppPartnerRequest): Promise<WhatsAppPartner> {
    const response = await api.put(`${getApiBase()}/whatsapp/partners/${id}`, data);
    return response.data.partner || response.data;
  },

  // 删除WhatsApp Partner
  async deleteWhatsAppPartner(id: number): Promise<void> {
    await api.delete(`${getApiBase()}/whatsapp/partners/${id}`);
  },

  // 测试WhatsApp Partner连接
  async testWhatsAppPartnerConnection(partnerId: number): Promise<TestConnectionResult> {
    const response = await api.post(`${getApiBase()}/whatsapp/partners/${partnerId}/test`);
    return response.data;
  },

  // 获取Partner配置模板
  async getPartnerConfigTemplates(): Promise<any> {
    const response = await api.get(`${getApiBase()}/whatsapp/partners/config-templates`);
    return response.data;
  }
};

// ========== Raw Messages 前端服务 ==========
export const rawMessageService = {
  async getRawMessages(params?: {
    page?: number;
    pageSize?: number;
    partnerType?: string;
    tenantCode?: string;
    phoneNumber?: string;
    requestId?: string;
  }): Promise<any> {
    const tenantCode = localStorage.getItem('currentTenant') || 'default';
    const queryParams = new URLSearchParams();
    if (params) Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null) queryParams.append(k, String(v)); });
    const qs = queryParams.toString();
    const url = qs ? `/${tenantCode}/whatsappapi/raw-messages?${qs}` : `/${tenantCode}/whatsappapi/raw-messages`;
    const response = await api.get(url);
    return response.data;
  },

  async getRawMessageById(id: number): Promise<any> {
    const tenantCode = localStorage.getItem('currentTenant') || 'default';
    const response = await api.get(`/${tenantCode}/whatsappapi/raw-messages/${id}`);
    return response.data;
  },

  async resendRawMessage(id: number): Promise<any> {
    const tenantCode = localStorage.getItem('currentTenant') || 'default';
    const response = await api.post(`/${tenantCode}/whatsappapi/raw-messages/${id}/resend`);
    return response.data;
  }
};
